import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    DropdownMultiSelectFilter,
    inject,
    NothingHere,
    SubTopBar,
    TicketsStore,
    Title,
    toArray,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsBoxArrowUpRight } from '@react-icons/all-files/bs/BsBoxArrowUpRight';
import { BsPlusSquare } from '@react-icons/all-files/bs/BsPlusSquare';
import { RouteComponentProps } from 'react-router';
import { Input } from 'reactstrap';
import { AccountsStore } from '../Accounts/AccountsStore';
import { ZendeskLink } from '../Core/ZendeskLink';
import { ZendeskLinkDropdown } from '../Core/ZendeskLinkDropdown';
import { DisplayFiltersStore } from '../DisplayFilters/DisplayFiltersStore';
import { TicketItem } from './TicketItem';

export interface IDateRange {
    name: string;
    startDate: Date;
    endDate: Date;
    format: string;
    groupByMonth: boolean;
}

export interface ITicketsListProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class TicketsList extends React.Component<ITicketsListProps> {
    @action
    public async componentDidMount() {
        this.displayFilter.accounts = this.displayFiltersStore.storage.displayFilter.accounts?.slice();
        this.loadData();
    }

    public render() {
        return (
            <div className="container-fluid pt-3">
                <Title>Tickets - Beta</Title>

                <SubTopBar>
                    <Input
                        type="text"
                        placeholder="Search Tickets..."
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                    />

                    <div className="mx-2 d-none d-md-block" />

                    {/*
                    {this.accountsStore.accounts.size > 1 && (
                        <div className="d-inline-flex">
                            <DropdownMultiSelectFilter
                                title="All Accounts"
                                options={toArray(this.accountsStore.accounts)}
                                filter={this.displayFilter.accounts}
                                print={this.printAccount}
                                sort={this.sortAccount}
                                comparer={this.compareAccount}
                                style={{
                                    minWidth: 400,
                                }}
                                className="border-top-0"
                                onToggle={this.onAccountToggle}
                                onChange={this.onAccountChange}
                                caret
                                nav
                            />

                            <div className="mx-2 border d-md-block" />
                        </div>
                    )}
                    */}

                    <ZendeskLinkDropdown />

                    <a className="btn btn-link d-flex" href="https://support.mri-inc.net/hc/en-us/requests">
                        Zendesk <BsBoxArrowUpRight style={{ fontSize: 21 }} className="float-end pt-1 ps-2 f-14" />
                    </a>
                </SubTopBar>

                <NothingHere
                    empty={!this.tickets.length}
                    icon={<BsPlusSquare />}
                    loading={this.loading}
                    label="No Tickets At This Time"
                    loadingMessage="Loading Tickets..."
                />

                {!!this.tickets.length &&
                    this.tickets.map((t: Mri.Tickets.Ticket) => (
                        <div className="col-12" key={t.ticketId}>
                            <TicketItem ticket={t} />
                        </div>
                    ))}
            </div>
        );
    }

    @computed
    private get search() {
        const search: Mri.Web.Internal.Controllers.Displays.SearchModel = {
            searchGuid: this.searchGuid,
            displayFilter: this.displayFilter,
            offset: 0,
            limit: 50,
            aggregates: false,
            config: false,
            status: false,
            software: false,
            telemetry: false,
            contract: false,
            orderBy: '',
            where: '',
        };

        return search;
    }

    @bind
    @action
    private onAccountToggle(isOpen: boolean) {
        if (!isOpen) {
            this.searchGuid = uuidv4generate();
            this.loadData();
        }
    }

    @bind
    @action
    private onAccountChange(value: Mri.DisplayFilters.Account[] | null | undefined) {
        this.displayFilter.accounts = value;
        this.displayFiltersStore.storage.displayFilter.accounts = value;
    }

    private printAccount(entity: Mri.Accounts.Account) {
        return entity.accountName;
    }

    private sortAccount(a: Mri.Accounts.Account, b: Mri.Accounts.Account) {
        return a.accountName.localeCompare(b.accountName);
    }

    private compareAccount(a: Mri.Accounts.Account, b: Mri.Accounts.Account) {
        return a.accountNumber == b.accountNumber;
    }

    @bind
    @action
    private async loadData() {
        this.loading = true;

        try {
            await this.accountsStore.loadAllAsync();
            await this.ticketsStore.loadTicketsAsync(this.search);
        } catch (e: any) {
            console.error(e);
        } finally {
            this.loading = false;
        }
    }

    @computed
    private get tickets() {
        let tickets = Array.from(this.ticketsStore.tickets, ([k, v]) => v);
        if (this.searchValue) tickets = tickets.filter((o: any) => deepStringFind(o, this.searchValue));
        return tickets;
    }

    @bind
    @action
    private onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @observable
    private displayFilter: Mri.DisplayFilters.DisplayFilter = {};

    @observable
    private loading = false;
    @observable
    private searchValue: string = '';
    private searchGuid = uuidv4generate();
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private displayFiltersStore!: DisplayFiltersStore;
    @inject
    private ticketsStore!: TicketsStore;
}
